'use client'

import { trackPricingFalKeyToggled } from '@/lib/analytics'
import { useEffect, useState, useSyncExternalStore, type ReactNode } from 'react'

// The one question that separates the two plan families is how generations are paid for, so
// the switch asks exactly that. Kept quiet, like a monthly/yearly toggle: the plan cards are
// the loud thing on this page.
// The NEW badge breathes a soft glow until someone opens the fal side, so the option is found,
// and the fal mark spins once every few seconds, at random.
const GLOW_CSS = `
@keyframes fal-new-glow { 0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); } 50% { box-shadow: 0 0 12px 2px rgba(255,255,255,0.55); } }
.fal-new-glow { animation: fal-new-glow 2.8s ease-in-out infinite; }
@keyframes fal-logo-spin { to { transform: rotate(360deg); } }
.fal-logo-spin { animation: fal-logo-spin 1.6s ease-in-out; }
@media (prefers-reduced-motion: reduce) { .fal-new-glow, .fal-logo-spin { animation: none; } }
`
const OPTIONS = [
  { own: false, label: 'Olives', line: 'Martini credits, included with paid plans. One bill.' },
  {
    own: true,
    label: 'Your own fal key',
    line: 'Bring your own bottle. Generations bill to your fal account at your fal rates.',
  },
]

// Remounted (new key) on every switch, so it starts mid-spin; otherwise it spins at random.
function FalLogo({ spinNow }: { spinNow: boolean }) {
  const [spinning, setSpinning] = useState(spinNow)
  useEffect(() => {
    if (spinning) return
    const t = setTimeout(() => setSpinning(true), 6000 + Math.random() * 10000)
    return () => clearTimeout(t)
  }, [spinning])
  return (
    // eslint-disable-next-line @next/next/no-img-element -- static brand mark
    <img
      src="/marketing/logos-mono/fal.svg"
      alt=""
      className={`h-4 w-4 ${spinning ? 'fal-logo-spin' : ''}`}
      onAnimationEnd={() => setSpinning(false)}
    />
  )
}

export function PlanTabs({ byok, olives }: { byok: ReactNode; olives: ReactNode }) {
  // /bar links here with ?plans=fal so the fal side is already open.
  const falFromUrl = useSyncExternalStore(
    () => () => {},
    () => new URLSearchParams(window.location.search).get('plans') === 'fal',
    () => false,
  )
  const [picked, setOwnKey] = useState<boolean | null>(null)
  const ownKey = picked ?? falFromUrl
  const [switches, setSwitches] = useState(0)
  const pick = (on: boolean) => {
    if (on === ownKey) return
    setOwnKey(on)
    setSwitches(n => n + 1)
    trackPricingFalKeyToggled({ on })
  }

  return (
    <div>
      <style>{GLOW_CSS}</style>
      <div className="mb-12 flex flex-col items-center gap-3">
        <p id="plan-type-label" className="text-[14px] text-white/50">
          Pay for generations with
        </p>
        <div
          role="group"
          aria-labelledby="plan-type-label"
          className="inline-flex rounded-full border border-white/15 bg-black/30 p-1 backdrop-blur-md"
        >
          {OPTIONS.map(o => {
            const on = o.own === ownKey
            return (
              <button
                key={o.label}
                type="button"
                aria-pressed={on}
                onClick={() => pick(o.own)}
                className={`inline-flex h-10 items-center gap-2 rounded-full px-5 text-[15px] font-medium transition-colors duration-150 ${
                  on ? 'bg-white/15 text-white' : 'text-white/55 hover:text-white'
                }`}
              >
                {o.own && <FalLogo key={switches} spinNow={switches > 0} />}
                {o.label}
                {o.own && (
                  <span
                    className={`rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-black ${
                      on ? '' : 'fal-new-glow'
                    }`}
                  >
                    New
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <p className="text-center text-[14px] text-white/45">{OPTIONS[ownKey ? 1 : 0].line}</p>
      </div>
      {/* Both stay mounted so PlanCards keeps its checkout state across switches. */}
      <div hidden={!ownKey}>{byok}</div>
      <div hidden={ownKey}>{olives}</div>
    </div>
  )
}
